import React, { useState, useMemo, useEffect } from "react";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import TuneIcon from "@mui/icons-material/Tune";
import TextField from "@mui/material/TextField";
import { toSnakeCase } from "../utils/general_utils";

const textFieldStyles = { width: "100%" };

export default function InputFilter(props) {
  const {
    item,
    name,
    data = [],
    icon,
    value: valueProp = null,
    callback,
    opt,
    defaultOpt = [],
    disabled = false,
  } = props;

  const [value, setValue] = useState(valueProp);
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const options = useMemo(() => {
    const detected = opt ?? data.map((row) => row?.[item]);
    const merged = [...defaultOpt, ...detected];

    const normalized = merged
      .filter((v) => v != null && v !== "")
      .map((v) => {
        if (typeof v === "object" && v !== null && "value" in v) {
          const vv = v.value;
          const ll = "label" in v ? v.label : undefined;
          const valueStr = vv == null ? "" : String(vv);
          if (!valueStr) return null;
          return {
            value: valueStr,
            label: ll != null && ll !== "" ? String(ll) : valueStr,
          };
        }
        return String(v);
      })
      .filter(Boolean);

    const map = new Map();
    for (const v of normalized) {
      const key = typeof v === "string" ? v : v.value;
      if (!map.has(key)) map.set(key, v);
    }

    const arr = Array.from(map.values());

    arr.sort((a, b) => {
      const aa = typeof a === "string" ? a : a.label;
      const bb = typeof b === "string" ? b : b.label;
      return aa.localeCompare(bb);
    });

    return arr;
  }, [data, opt, defaultOpt, item]);

  useEffect(() => {
    const exists = options.some((o) => {
      const optVal = typeof o === "string" ? o : o.value;
      return String(optVal) === String(value);
    });

    if (value != null && !exists) {
      setValue(null);
      callback?.(item, null);
    }
  }, [options]);

  useEffect(() => {
    setValue(valueProp ?? null);
  }, [valueProp]);

  const selectedOption = useMemo(() => {
    if (value == null) return null;

    const found = options.find((o) => {
      const optVal = typeof o === "string" ? o : o.value;
      return String(optVal) === String(value);
    });

    return found ?? String(value);
  }, [options, value]);

  return (
    <FormControl sx={textFieldStyles} size="small">
      <Autocomplete
        disabled={disabled}
        test_id={toSnakeCase("autocomplete_" + name)}
        size="small"
        options={options}
        value={selectedOption}
        onChange={(event, newValue) => {
          const next =
            newValue == null
              ? null
              : typeof newValue === "string"
                ? newValue
                : newValue.value; 

          setValue(next);
          callback?.(item, next);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        getOptionLabel={(option) =>
          option == null
            ? ""
            : typeof option === "string"
              ? String(option)
              : String(option.label ?? option.value)
        }
        isOptionEqualToValue={(optItem, valItem) => {
          const optVal = typeof optItem === "string" ? optItem : optItem.value;
          const valVal =
            valItem == null
              ? ""
              : typeof valItem === "string"
                ? valItem
                : valItem.value;

          return String(optVal) === String(valVal);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={disabled}
            test_id={toSnakeCase(name)}
            label={name}
            InputLabelProps={{
              shrink: isFocused || inputValue.length > 0,
              sx: {
                marginLeft: inputValue.length > 0 || isFocused ? 0 : "24px",
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {icon ?? <TuneIcon />}
                  {params.InputProps?.startAdornment}
                </>
              ),
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        )}
      />
    </FormControl>
  );
}